import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentCard = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [downloading, setDownloading] = useState(null);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Student and Cards Data
  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    course: "...",
    department: "...",
    batch: "...",
    rollNumber: "...",
    profileImage: null
  });

  const [cardsData, setCardsData] = useState([]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch Student Cards Data from API
  useEffect(() => {
    const fetchCardsData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch student cards data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          console.log('❌ No token found');
          setError('Authentication token not found. Please login again.');
          setStudentData(prev => ({
            ...prev,
            name: userInfo?.name || "Student User",
            studentId: userInfo?.id || "SVU2024001"
          }));
          setLoading(false);
          return;
        }

        // First get student profile for basic info
        try {
          const profileResponse = await fetch(`${API_LINK}/api/student/profile`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (profileResponse.ok) {
            const profileResult = await profileResponse.json();
            if (profileResult.success) {
              setStudentData({
                name: profileResult.data.name,
                studentId: profileResult.data.studentId,
                course: profileResult.data.course,
                department: profileResult.data.department,
                batch: profileResult.data.batch,
                rollNumber: profileResult.data.rollNumber,
                profileImage: profileResult.data.profileImage
              });
              console.log('✅ Student profile loaded:', profileResult.data.name);
            }
          }
        } catch (profileError) {
          console.error('Error fetching profile:', profileError);
        }

        // ✅ FIXED: Fetch student cards from CORRECT endpoint
        try {
          console.log('📡 Calling student cards API...');
          const cardsResponse = await fetch(`${API_LINK}/api/student/cards`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          console.log('📡 Cards API response status:', cardsResponse.status);

          if (cardsResponse.ok) {
            const cardsResult = await cardsResponse.json();
            console.log('📡 Cards API response:', cardsResult);

            if (cardsResult.success) {
              const cards = cardsResult.data?.cards || [];
              console.log('✅ Student cards fetched successfully:', cards.length, 'cards found');
              setCardsData(cards);
            } else {
              console.log('⚠️ Cards API returned success=false:', cardsResult.message);
              setCardsData([]);
            }
          } else {
            const errorText = await cardsResponse.text();
            console.log('❌ Cards API failed with status:', cardsResponse.status);
            console.log('❌ Error response:', errorText);

            if (cardsResponse.status === 404) {
              console.log('ℹ️ Cards endpoint not found - student may not have any cards assigned');
              setCardsData([]);
            } else {
              setError(`Failed to fetch cards: ${cardsResponse.status} - ${errorText}`);
            }
          }
        } catch (cardsError) {
          console.error('❌ Error fetching student cards:', cardsError);
          console.log('ℹ️ Setting empty cards array due to fetch error');
          setCardsData([]);
        }

        console.log('✅ Cards data fetch completed');

      } catch (error) {
        console.error('💥 Error fetching cards data:', error);
        setError(`Failed to load cards data: ${error.message}`);

        // Fallback to userInfo if available
        if (userInfo) {
          console.log('🔄 Using fallback userInfo data');
          setStudentData(prev => ({
            ...prev,
            name: userInfo.name || prev.name,
            studentId: userInfo.id || prev.studentId
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    // Add a small delay to ensure component is mounted
    const timer = setTimeout(fetchCardsData, 100);
    return () => clearTimeout(timer);
  }, [userInfo]);

  // Handle real card download
  const handleDownload = async (card) => {
    try {
      setDownloading(card.id);

      console.log(`📥 Downloading ${card.card_name}...`);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!token) {
        alert('Authentication required. Please login again.');
        return;
      }

      // ✅ FIXED: Use correct student cards download endpoint
      const response = await fetch(`${API_LINK}/api/student/cards/download/${card.id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Create blob from response
        const blob = await response.blob();

        // Create download link
        const downloadUrl = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `${card.card_name}.pdf`;
        downloadLink.style.display = 'none';

        // Add to DOM, click, and remove
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Clean up the blob URL
        window.URL.revokeObjectURL(downloadUrl);

        console.log(`✅ Card download successful: ${card.card_name}`);

        // Show success message
        setTimeout(() => {
          alert(`📄 ${card.card_name} downloaded successfully! Check your downloads folder.`);
        }, 500);
      } else {
        const errorResult = await response.json().catch(() => ({ message: 'Download failed' }));
        throw new Error(errorResult.message || 'Download failed');
      }

    } catch (error) {
      console.error('❌ Card download failed:', error);
      alert(`❌ Failed to download ${card.card_name}. ${error.message}`);
    } finally {
      setDownloading(null);
    }
  };





  // Get card type icon
  const getCardTypeIcon = (cardType) => {
    const icons = {
      library: '📚',
      id_card: '🆔',
      admit: '🎓',
      registration: '📝',
      sports: '⚽',
      hostel: '🏠',
      transport: '🚌',
      medical: '🏥',
      other: '📄'
    };
    return icons[cardType] || '📄';
  };

  // Get card type color
  const getCardTypeColor = (cardType) => {
    const colors = {
      library: '#28a745',
      id_card: '#007bff',
      admit: '#17a2b8',
      registration: '#6c757d',
      sports: '#fd7e14',
      hostel: '#e83e8c',
      transport: '#20c997',
      medical: '#dc3545',
      other: '#6f42c1'
    };
    return colors[cardType] || '#6c757d';
  };

  // Get card type display name
  const getCardTypeDisplayName = (cardType) => {
    const names = {
      library: 'Library Card',
      id_card: 'Student ID Card',
      admit: 'Admit Card',
      registration: 'Registration Card',
      sports: 'Sports Card',
      hostel: 'Hostel Card',
      transport: 'Transport Card',
      medical: 'Medical Card',
      other: 'Other Card'
    };
    return names[cardType] || 'Card';
  };

  // Check if card is expired
  const isCardExpired = (card) => {
    if (!card.expiry_date) return false;
    return new Date(card.expiry_date) < new Date();
  };

  // Check if card is expiring soon (within 30 days)
  const isCardExpiringSoon = (card) => {
    if (!card.expiry_date) return false;
    const expiryDate = new Date(card.expiry_date);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiryDate <= thirtyDaysFromNow && expiryDate > new Date();
  };



  // University Logo Component
  const SVULogo = () => (
    <img
      src="/logo.png"
      alt="Swami Vivekananda University Logo"
      style={{
        width: isMobile ? '40px' : '120px',
        height: isMobile ? '40px' : '120px',
        objectFit: 'contain',
        transition: 'transform 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'scale(1)';
      }}
    />
  );

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Student Cards...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error && !studentData.name) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Student Cards
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Try Again
            </button>

            <button
              onClick={() => window.location.href = '/'}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-cards"
      pageTitle="Student Cards"
    >
      {/* Student Cards Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          background: '#238548',
          color: '#ffffff',
          padding: isMobile ? '1.2rem 1rem' : '2rem',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: isMobile ? '0.8rem' : '1rem'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            gap: isMobile ? '0.6rem' : '2rem',
            flexWrap: 'wrap'
          }}>
            <div style={{
              width: isMobile ? '70px' : '100px',
              height: isMobile ? '70px' : '100px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? '1.8rem' : '2.5rem',
              fontWeight: 'bold',
              border: isMobile ? '3px solid rgba(255, 255, 255, 0.3)' : '4px solid rgba(255, 255, 255, 0.3)',
              flexShrink: 0,
              overflow: 'hidden',
              backgroundImage: studentData.profileImage ? `url(${API_LINK}${studentData.profileImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}>
              {!studentData.profileImage && (studentData.name ? studentData.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : 'ST')}
            </div>
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <h1 style={{
                margin: isMobile ? '0 0 0.3rem 0' : '0 0 0.5rem 0',
                fontSize: isMobile ? '1.3rem' : '2rem',
                fontWeight: 'bold'
              }}>
                {studentData.name}
              </h1>
              <p style={{
                margin: '0 0 0.3rem 0',
                fontSize: isMobile ? '0.8rem' : '1.1rem',
                opacity: 0.9
              }}>
                ID: {studentData.studentId} • Roll: {studentData.rollNumber}
              </p>
              <p style={{
                margin: 0,
                fontSize: isMobile ? '0.75rem' : '1rem',
                opacity: 0.8
              }}>
                {studentData.course} • {studentData.batch}
              </p>
            </div>
          </div>
          <div style={{
            textAlign: isMobile ? 'center' : 'right',
            flexShrink: 0
          }}>
            <h2 style={{
              margin: isMobile ? '0 0 0.2rem 0' : '0 0 0.5rem 0',
              fontSize: isMobile ? '1.1rem' : '1.5rem'
            }}>
              Your Student Cards
            </h2>
            <p style={{
              margin: 0,
              fontSize: isMobile ? '0.75rem' : '1rem',
              opacity: 0.8
            }}>
              {cardsData.length} card{cardsData.length !== 1 ? 's' : ''} available
            </p>
          </div>
        </div>
      </div>

      {/* Cards Grid or No Cards Message */}
      {cardsData.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '1.5rem'
        }}>
          {cardsData.map((card) => {
            const cardExpired = isCardExpired(card);
            const cardExpiringSoon = isCardExpiringSoon(card);
            const cardColor = getCardTypeColor(card.card_type);

            return (
              <div key={card.id} style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: cardExpired ? '2px solid #dc3545' : cardExpiringSoon ? '2px solid #ffc107' : `2px solid ${cardColor}`,
                opacity: card.status === 'inactive' ? 0.7 : 1
              }}
                onMouseEnter={(e) => {
                  if (card.status === 'active') {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                }}>
                {/* Card Header */}
                <div style={{
                  background: cardExpired ? '#dc3545' : cardExpiringSoon ? '#ffc107' : cardColor,
                  color: '#ffffff',
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <span style={{ fontSize: '2rem' }}>
                      {getCardTypeIcon(card.card_type)}
                    </span>
                    <h3 style={{ margin: 0, fontSize: '1.2rem' }}>
                      {card.card_name}
                    </h3>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: '#ffffff'
                    }}>
                      {card.status}
                    </span>
                    {cardExpired && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        color: '#ffffff'
                      }}>
                        EXPIRED
                      </span>
                    )}
                    {cardExpiringSoon && !cardExpired && (
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: '500',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        color: '#ffffff'
                      }}>
                        EXPIRING SOON
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Content */}
                <div style={{ padding: '1.5rem' }}>
                  <p style={{
                    margin: '0 0 1rem 0',
                    color: '#666',
                    fontSize: '0.95rem'
                  }}>
                    {getCardTypeDisplayName(card.card_type)} issued by administration
                  </p>

                  {/* Card Details */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div>
                      <span style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        color: '#999',
                        marginBottom: '0.25rem'
                      }}>
                        Issued On
                      </span>
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: '500',
                        color: '#333'
                      }}>
                        {new Date(card.issued_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        color: '#999',
                        marginBottom: '0.25rem'
                      }}>
                        {card.expiry_date ? 'Valid Until' : 'Status'}
                      </span>
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: '500',
                        color: cardExpired ? '#dc3545' : cardExpiringSoon ? '#ffc107' : '#28a745'
                      }}>
                        {card.expiry_date
                          ? new Date(card.expiry_date).toLocaleDateString()
                          : 'No Expiry'
                        }
                      </span>
                    </div>
                  </div>

                  {/* Download Stats */}
                  {card.download_count > 0 && (
                    <div style={{
                      background: '#f8f9fa',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      marginBottom: '1.5rem',
                      fontSize: '0.85rem',
                      color: '#666'
                    }}>
                      Downloaded {card.download_count} time{card.download_count !== 1 ? 's' : ''}
                      {card.last_downloaded && (
                        <span> • Last: {new Date(card.last_downloaded).toLocaleDateString()}</span>
                      )}
                    </div>
                  )}



                  {/* Action Button */}
                  <div>
                    <button
                      onClick={() => handleDownload(card)}
                      disabled={card.status !== 'active' || downloading === card.id}
                      style={{
                        width: '100%',
                        background: card.status !== 'active' ? '#6c757d' : downloading === card.id ? '#6c757d' : cardColor,
                        color: '#ffffff',
                        border: 'none',
                        padding: '0.75rem 1rem',
                        borderRadius: '6px',
                        cursor: card.status !== 'active' || downloading === card.id ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        opacity: card.status !== 'active' || downloading === card.id ? 0.7 : 1
                      }}
                    >
                      {downloading === card.id ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #ffffff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          Downloading...
                        </>
                      ) : card.status !== 'active' ? (
                        '🚫 Card Inactive'
                      ) : (
                        <>📥 Download {getCardTypeDisplayName(card.card_type)}</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // No Cards Available Message
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          padding: '3rem',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1.5rem',
            color: '#6c757d'
          }}>
            🆔
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            No Student Cards Assigned
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5,
            maxWidth: '500px',
            margin: '0 auto 2rem auto'
          }}>
            You don't have any student cards assigned yet. Cards like Library Card, Student ID,
            Admit Cards, etc. will appear here once they are assigned by the administration.
          </p>

          <div style={{
            background: '#e8f5e8',
            padding: '1.5rem',
            borderRadius: '8px',
            color: '#238548',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            maxWidth: '400px',
            margin: '0 auto'
          }}>
            <strong>💡 What to do:</strong><br />
            Contact your department office or student services to request the cards you need.
            They will assign the appropriate cards to your account.
          </div>
        </div>
      )}

      {/* Quick Info */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        padding: '2rem',
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <h3 style={{
          margin: '0 0 1rem 0',
          color: '#333',
          fontSize: '1.3rem'
        }}>
          📋 Important Information
        </h3>
        <p style={{
          margin: 0,
          color: '#666',
          fontSize: '1rem',
          lineHeight: 1.6
        }}>
          All cards are available in PDF format and are digitally signed by the institution.
          Keep your downloaded cards safe and secure. For any issues with downloading or card verification,
          please contact the student helpdesk or the respective department.
        </p>
      </div>
    </Layout>
  );
};

export default StudentCard;